/**
 * O que faz: permite revisar/salvar fatos da clínica por texto, PDF privado ou gravação transcrita.
 * Onde usa: página Inteligência Artificial > Base de conhecimento.
 * Conexões: TanStack Query, servico.ts, gravador e modais compartilhados do design system.
 * Tabelas: clinica_conhecimentos via serviço com RLS; PDFs em Storage privado.
 * Data: 29/09/2026.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, FileText, Loader2, Mic, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { GravadorConhecimentoClinica } from './GravadorConhecimentoClinica';
import { AreaUploadPdfConhecimento } from './AreaUploadPdfConhecimento';
import {
  definirConhecimentoAtivo, excluirConhecimento, listarConhecimentos, mensagemErroConhecimento,
  obterPdfPrivado, processarArquivoConhecimento, salvarConhecimento, type ItemConhecimento, type RascunhoConhecimento,
} from './servico';
import { detectarInstrucaoSuspeita, validarTextoConhecimento, type OrigemConhecimento } from '../../../supabase/functions/_shared/clinic-knowledge';

const ROTULOS_ORIGEM = { texto: 'Texto digitado', pdf: 'PDF', audio: 'Áudio transcrito' };
const ORIGENS = [
  { valor: 'texto' as const, titulo: 'Escrever texto', descricao: 'Cadastre informações, serviços e orientações da clínica.', icone: Pencil },
  { valor: 'pdf' as const, titulo: 'Anexar PDF', descricao: 'Extraia e revise o conteúdo de um documento da clínica.', icone: Upload },
  { valor: 'audio' as const, titulo: 'Gravar áudio', descricao: 'Conte as informações e revise a transcrição antes de salvar.', icone: Mic },
];

export function BaseConhecimentoClinica({ clinicaId }: { clinicaId: string }) {
  const queryClient = useQueryClient();
  const [rascunho, setRascunho] = useState<RascunhoConhecimento | null>(null);
  const [suspeito, setSuspeito] = useState(false);
  const [processando, setProcessando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [microfoneAtivo, setMicrofoneAtivo] = useState(false);
  const [acaoPendente, setAcaoPendente] = useState<string | null>(null);
  const [exclusao, setExclusao] = useState<ItemConhecimento | null>(null);
  const [erroEditor, setErroEditor] = useState<string | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState<string | null>(null);
  const versaoEditor = useRef(0);
  const montado = useRef(true);
  const botaoAberturaEditor = useRef<HTMLElement | null>(null);
  // Nenhuma resposta assíncrona deve reabrir editor ou publicar rascunho após sair/trocar clínica.
  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);
  const consulta = useQuery({
    queryKey: ['clinica-conhecimentos', clinicaId],
    queryFn: () => listarConhecimentos(clinicaId),
    staleTime: 0,
    retry: false,
  });
  const itens = consulta.data ?? [];
  const ativos = itens.filter((item) => item.ativo);
  const caracteresAtivos = ativos.reduce((total, item) => total + item.conteudo.length, 0);
  const atualizar = () => queryClient.invalidateQueries({ queryKey: ['clinica-conhecimentos', clinicaId] });

  const abrirEditor = (origem: OrigemConhecimento, item?: ItemConhecimento) => {
    // Os cartões ficam fora de DialogTrigger: guardamos a origem para devolver o foco após fechar.
    botaoAberturaEditor.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    versaoEditor.current++;
    setErroEditor(null);
    setSuspeito(false);
    setMicrofoneAtivo(false);
    setNomeArquivo(item?.fonte_nome ?? null);
    setRascunho(item ? { id: item.id, titulo: item.titulo, conteudo: item.conteudo, origem: item.origem, fonteNome: item.fonte_nome }
      : { titulo: '', conteudo: '', origem });
  };
  const fecharEditor = () => {
    // Salvar não é cancelável depois do INSERT; aguarda conclusão para evitar duplicar o clique.
    if (salvando) return;
    versaoEditor.current++;
    setRascunho(null);
    setProcessando(false);
  };

  const importar = async (arquivo: File, origem: 'pdf' | 'audio') => {
    const versaoAtual = versaoEditor.current;
    setProcessando(true);
    setErroEditor(null);
    setNomeArquivo(arquivo.name);
    try {
      const resultado = await processarArquivoConhecimento(clinicaId, origem, arquivo);
      if (!montado.current || versaoAtual !== versaoEditor.current) return;
      setSuspeito(resultado.suspeito);
      // Só preenche o editor: nunca salva uma transcrição ou extração sem confirmação humana.
      setRascunho((atual) => atual ? { ...atual, origem, arquivo: origem === 'pdf' ? arquivo : undefined,
        fonteNome: arquivo.name, conteudo: resultado.conteudo,
        titulo: atual.titulo || (origem === 'pdf' ? arquivo.name.replace(/\.pdf$/i, '').slice(0, 120) : 'Informações da clínica') } : null);
    } catch (erro) {
      if (montado.current && versaoAtual === versaoEditor.current) setErroEditor(mensagemErroConhecimento(erro));
    } finally {
      if (montado.current && versaoAtual === versaoEditor.current) setProcessando(false);
    }
  };

  const salvar = async () => {
    if (!rascunho || salvando || processando || microfoneAtivo) return;
    setErroEditor(null);
    try {
      // Antes do upload/INSERT valida texto e orçamento; o banco repete a guarda com trava concorrente.
      validarTextoConhecimento(rascunho.titulo, rascunho.conteudo);
      const anterior = itens.find((item) => item.id === rascunho.id);
      const seraAtivo = anterior ? anterior.ativo : true;
      const totalNovo = caracteresAtivos - (anterior?.ativo ? anterior.conteudo.length : 0) + (seraAtivo ? rascunho.conteudo.trim().length : 0);
      if (totalNovo > 60_000) throw new Error('A base ativa permite até 60.000 caracteres. Desative ou resuma conteúdos.');
      if (!rascunho.id && itens.length >= 50) throw new Error('A base permite até 50 conteúdos por clínica.');
      setSalvando(true);
      await salvarConhecimento(clinicaId, rascunho);
      await atualizar();
      if (montado.current) { setRascunho(null); toast.success('Conhecimento salvo. Será usado nos próximos follow-ups gerados por IA.'); }
    } catch (erro) {
      if (montado.current) setErroEditor(mensagemErroConhecimento(erro));
    } finally { if (montado.current) setSalvando(false); }
  };

  const alternar = async (item: ItemConhecimento) => {
    setAcaoPendente(item.id);
    try {
      await definirConhecimentoAtivo(clinicaId, item.id, !item.ativo);
      await atualizar();
      toast.success(item.ativo ? 'Conteúdo desativado para os próximos follow-ups.' : 'Conteúdo ativado para os próximos follow-ups.');
    } catch (erro) { toast.error(mensagemErroConhecimento(erro)); }
    finally { if (montado.current) setAcaoPendente(null); }
  };

  const confirmarExclusao = async () => {
    if (!exclusao) return;
    setAcaoPendente(exclusao.id);
    try {
      await excluirConhecimento(clinicaId, exclusao);
      setExclusao(null);
      toast.success('Conhecimento excluído.');
    } catch (erro) { toast.error(mensagemErroConhecimento(erro)); }
    finally { await atualizar(); if (montado.current) setAcaoPendente(null); }
  };

  const abrirPdf = async (item: ItemConhecimento) => {
    // Abre no gesto do usuário para evitar bloqueio de popup; remove opener antes de carregar a URL.
    const janela = window.open('about:blank', '_blank');
    if (janela) janela.opener = null;
    try {
      const url = await obterPdfPrivado(clinicaId, item);
      if (janela) janela.location.replace(url);
      else toast.error('Permita abrir uma nova aba no navegador para ver o PDF.');
    } catch (erro) { janela?.close(); toast.error(mensagemErroConhecimento(erro)); }
  };

  return (
    <section className="space-y-6" aria-labelledby="titulo-base-conhecimento">
      <div className="space-y-2">
        <h2 id="titulo-base-conhecimento" className="text-xl font-semibold">O que a IA precisa saber sobre sua clínica</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">Reúna serviços, horários, localização e regras de atendimento. A base ativa é consultada em cada novo follow-up gerado por IA, inclusive nas campanhas já ativadas.</p>
        <p className="text-xs text-muted-foreground">Mensagens fixas e templates oficiais mantêm o texto cadastrado. O assistente de atendimento atual ainda usa sua própria configuração.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {ORIGENS.map((origem) => <button key={origem.valor} type="button" disabled={consulta.isPending || consulta.isError || itens.length >= 50}
          onClick={() => abrirEditor(origem.valor)} className="lg-card flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
          <origem.icone className="h-5 w-5 text-accent-foreground" aria-hidden="true" />
          <span className="font-semibold">{origem.titulo}</span><span className="text-sm text-muted-foreground">{origem.descricao}</span>
        </button>)}
      </div>
      <div className="rounded-2xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
        Use apenas informações da clínica, sem dados de pacientes. Textos, PDFs e transcrições passam por revisão e checagem de comandos suspeitos; eles não podem mudar as regras da IA.
      </div>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
          <h3 className="flex items-center gap-2 font-semibold"><BookOpen className="h-4 w-4" aria-hidden="true" />Conteúdos da clínica</h3>
          <p className="text-xs text-muted-foreground">{ativos.length} ativos · {caracteresAtivos.toLocaleString('pt-BR')} / 60.000 caracteres · {itens.length} / 50 conteúdos</p>
        </div>
        {consulta.isPending ? <p role="status" className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />Carregando a base de conhecimento…</p>
          : consulta.isError ? <div role="alert" className="space-y-3 py-6"><p className="text-sm text-destructive">{mensagemErroConhecimento(consulta.error)}</p><Button variant="outline" onClick={() => void consulta.refetch()}>Tentar novamente</Button></div>
          : !itens.length ? <div className="space-y-3 py-8 text-center"><BookOpen className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" /><p className="font-medium">Sua base ainda está vazia</p><p className="text-sm text-muted-foreground">Comece com uma apresentação da clínica e seus serviços.</p><Button variant="outline" onClick={() => abrirEditor('texto')}><Plus className="mr-2 h-4 w-4" />Adicionar primeiro texto</Button></div>
          : <ul className="divide-y divide-border">
            {itens.map((item) => <li key={item.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-1"><p className="break-words font-medium">{item.titulo}</p><p className="text-xs text-muted-foreground">{ROTULOS_ORIGEM[item.origem]} · Atualizado em {new Date(item.updated_at).toLocaleDateString('pt-BR')} · {item.ativo ? 'Ativo no follow-up' : 'Desativado'}</p><p className="line-clamp-2 max-w-2xl break-words text-sm text-muted-foreground">{item.conteudo}</p></div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Switch checked={item.ativo} disabled={acaoPendente !== null} aria-label={`Usar ${item.titulo} no follow-up`} onCheckedChange={() => void alternar(item)} />
                {item.origem === 'pdf' && <Button variant="ghost" size="sm" onClick={() => void abrirPdf(item)}><FileText className="mr-1 h-4 w-4" />PDF</Button>}
                <Button variant="ghost" size="icon" disabled={acaoPendente !== null} aria-label={`Editar ${item.titulo}`} onClick={() => abrirEditor(item.origem, item)}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" disabled={acaoPendente !== null} aria-label={`Excluir ${item.titulo}`} onClick={() => setExclusao(item)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </li>)}
          </ul>}
      </div>
      {/* A área de formulário já existente é dona do scroll: sua timeline não pode controlar
          a animação de saída do DialogContent, pois o Radix espera o término para liberar foco. */}
      <Dialog open={rascunho !== null} onOpenChange={(aberto) => { if (!aberto) fecharEditor(); }}>
        <DialogContent className="flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-2xl flex-col overflow-hidden"
          onCloseAutoFocus={(evento) => {
            // Preserva a navegação por teclado sem disputar foco caso a clínica/tela tenha sido desmontada.
            if (botaoAberturaEditor.current?.isConnected) {
              evento.preventDefault();
              botaoAberturaEditor.current.focus();
            }
          }}
          onEscapeKeyDown={(evento) => { if (salvando) evento.preventDefault(); }} onInteractOutside={(evento) => { if (salvando) evento.preventDefault(); }}>
          <DialogHeader><DialogTitle>{rascunho?.id ? 'Editar conhecimento' : rascunho?.origem === 'pdf' ? 'Adicionar conhecimento por PDF' : rascunho?.origem === 'audio' ? 'Adicionar conhecimento por áudio' : 'Adicionar conhecimento por texto'}</DialogTitle><DialogDescription>Confira os fatos e remova qualquer comando à IA antes de salvar. O conteúdo salvo entra na base ativa.</DialogDescription></DialogHeader>
          {rascunho && <div className="scroll-fade min-h-0 space-y-4 overflow-y-auto">
            {/* Clique e drop passam pelo mesmo processamento e continuam exigindo revisão antes de salvar. */}
            {!rascunho.id && rascunho.origem === 'pdf' && <AreaUploadPdfConhecimento ocupado={processando || salvando} processando={processando} nomeArquivo={nomeArquivo} aoSelecionar={(arquivo) => void importar(arquivo, 'pdf')} />}
            {!rascunho.id && rascunho.origem === 'audio' && <GravadorConhecimentoClinica ocupado={processando || salvando} aoMudarGravacao={setMicrofoneAtivo} aoConcluir={(arquivo) => void importar(arquivo, 'audio')} />}
            {processando && <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />{rascunho.origem === 'pdf' ? 'Extraindo o texto do PDF…' : 'Transcrevendo o áudio…'}</p>}
            <div className="space-y-2"><Label htmlFor="titulo-conhecimento">Título</Label><Input id="titulo-conhecimento" value={rascunho.titulo} maxLength={120} disabled={salvando || processando} placeholder="Ex.: Serviços e horários da clínica" onChange={(evento) => setRascunho({ ...rascunho, titulo: evento.target.value })} /></div>
            <div className="space-y-2"><Label htmlFor="conteudo-conhecimento">{rascunho.origem === 'texto' ? 'Informações da clínica' : 'Revise o texto antes de salvar'}</Label><Textarea id="conteudo-conhecimento" className="scroll-fade min-h-48" value={rascunho.conteudo} maxLength={20_000} disabled={salvando || processando} placeholder="Descreva serviços, endereço, horários, diferenciais e regras de atendimento…" onChange={(evento) => { setRascunho({ ...rascunho, conteudo: evento.target.value }); setSuspeito(detectarInstrucaoSuspeita(evento.target.value)); }} /><p className="text-xs text-muted-foreground">{rascunho.conteudo.length.toLocaleString('pt-BR')} / 20.000 caracteres</p></div>
            {suspeito && <p role="alert" className="text-sm text-destructive">Identificamos uma possível instrução maliciosa. Remova comandos à IA e mantenha apenas fatos da clínica.</p>}
            {erroEditor && <p role="alert" className="text-sm text-destructive">{erroEditor}</p>}
            <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4"><Button variant="outline" disabled={salvando} onClick={fecharEditor}>Cancelar</Button><Button disabled={salvando || processando || microfoneAtivo || suspeito || !rascunho.titulo.trim() || !rascunho.conteudo.trim()} onClick={() => void salvar()}>{salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" /> : null}{salvando ? 'Salvando…' : 'Salvar na base'}</Button></div>
          </div>}
        </DialogContent>
      </Dialog>
      <AlertDialog open={exclusao !== null} onOpenChange={(aberto) => { if (!aberto && !acaoPendente) setExclusao(null); }}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Excluir este conhecimento?</AlertDialogTitle><AlertDialogDescription>“{exclusao?.titulo}” deixará de ser usado nos próximos follow-ups. O PDF anexado também será excluído. Esta ação não altera mensagens já enviadas.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel disabled={acaoPendente !== null}>Cancelar</AlertDialogCancel><Button variant="destructive" disabled={acaoPendente !== null} onClick={() => void confirmarExclusao()}>{acaoPendente ? 'Excluindo…' : 'Excluir conhecimento'}</Button></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
